import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  RotateCcw,
  Volume2,
  Check,
  Sparkles,
  Lightbulb,
  Shuffle,
} from 'lucide-react';

/**
 * A1 — Remets les mots dans l'ordre !
 * Scrambled-chunks → typed sentence reconstruction.
 * Correct answer hidden until learner submits.
 */
const A1_EXERCISES = [
  { id: 'a1-rmo-1', chunks: ['un chat', 'Marie', 'a'], answer: "Marie a un chat." },
  { id: 'a1-rmo-2', chunks: ['est', 'Paris', 'en France'], answer: 'Paris est en France.' },
  { id: 'a1-rmo-3', chunks: ['le matin', 'je', 'bois', 'un café'], answer: 'Je bois un café le matin.' },
  { id: 'a1-rmo-4', chunks: ['allons', 'nous', "à l'école"], answer: "Nous allons à l'école." },
  { id: 'a1-rmo-5', chunks: ['tu', 'es', 'très gentil'], answer: 'Tu es très gentil.' },
  { id: 'a1-rmo-6', chunks: ['est', 'mon frère', 'très grand'], answer: 'Mon frère est très grand.' },
  { id: 'a1-rmo-7', chunks: ['avons', 'nous', 'une grande famille'], answer: 'Nous avons une grande famille.' },
  { id: 'a1-rmo-8', chunks: ['un livre', 'lis', 'je'], answer: 'Je lis un livre.' },
  { id: 'a1-rmo-9', chunks: ['avez', 'vous', 'deux enfants'], answer: 'Vous avez deux enfants.' },
  { id: 'a1-rmo-10', chunks: ['froid', 'il', 'fait'], answer: 'Il fait froid.' },
];

const A2_EXERCISES = [
  { id: 'a2-rmo-1', chunks: ['chaque matin', 'prend', 'il', 'son petit-déjeuner'], answer: 'Il prend son petit-déjeuner chaque matin.' },
  { id: 'a2-rmo-2', chunks: ['travaille', 'depuis', 'il', 'deux ans', 'ici'], answer: 'Il travaille ici depuis deux ans.' },
  { id: 'a2-rmo-3', chunks: ['du tout', "n'aime pas", 'je', 'le café'], answer: "Je n'aime pas le café du tout." },
  { id: 'a2-rmo-4', chunks: ['se promènent', 'parc', 'dans le', 'ils'], answer: 'Ils se promènent dans le parc.' },
  { id: 'a2-rmo-5', chunks: ['ont', 'terminé', 'ils', 'le projet', 'déjà'], answer: 'Ils ont déjà terminé le projet.' },
  { id: 'a2-rmo-6', chunks: ['nouveau', 'a', 'acheté', 'un', 'téléphone', 'elle'], answer: 'Elle a acheté un nouveau téléphone.' },
  { id: 'a2-rmo-7', chunks: ['ne vais pas', 'sortir', 'je', 'ce soir'], answer: 'Je ne vais pas sortir ce soir.' },
  { id: 'a2-rmo-8', chunks: ['va', 'cuisiner', 'un gâteau', 'elle'], answer: 'Elle va cuisiner un gâteau.' },
  { id: 'a2-rmo-9', chunks: ['commence', 'tôt', 'trop', 'pour moi', 'la réunion'], answer: 'La réunion commence trop tôt pour moi.' },
  { id: 'a2-rmo-10', chunks: ['avons', 'réservé', 'une table', 'nous', 'pour deux'], answer: 'Nous avons réservé une table pour deux.' },
];

const B1_EXERCISES = [
  { id: 'b1-rmo-1', chunks: ['partirions', 'demain', 'si', 'pouvions', 'nous', 'nous'], answer: 'Nous partirions demain si nous pouvions.' },
  { id: 'b1-rmo-2', chunks: ['important', 'est', 'que', 'tu', 'sois', "à l'heure", 'il'], answer: "Il est important que tu sois à l'heure." },
  { id: 'b1-rmo-3', chunks: ['en', 'déjà', 'avons', 'trois', 'acheté', 'nous'], answer: 'Nous en avons déjà acheté trois.' },
  { id: 'b1-rmo-4', chunks: ['lisait', 'pendant que', 'elle', 'je', 'cuisinais'], answer: 'Elle lisait pendant que je cuisinais.' },
  { id: 'b1-rmo-5', chunks: ['a dit', 'que', 'il', 'venir', 'ne pouvait pas'], answer: "Il a dit qu'il ne pouvait pas venir." },
  { id: 'b1-rmo-6', chunks: ['finirai', 'je', 'quand', 'ce travail ?'], answer: 'Quand finirai-je ce travail ?' },
  { id: 'b1-rmo-7', chunks: ['en', 'il', 'parlant', 'plus', 'comprendra'], answer: 'Il comprendra en parlant plus.' },
  { id: 'b1-rmo-8', chunks: ['je', 'lui', 'demain', 'parlerai', 'y', 'avec'], answer: "J'y parlerai demain avec lui." },
  { id: 'b1-rmo-9', chunks: ['est', 'plus', 'ce problème', 'compliqué', 'que', 'prévu'], answer: 'Ce problème est plus compliqué que prévu.' },
  { id: 'b1-rmo-10', chunks: ['avions déjà', 'quand', 'nous', 'commencé', 'il', 'était parti'], answer: 'Il était déjà parti quand nous avions commencé.' },
];

const B2_EXERCISES = [
  { id: 'b2-rmo-1', chunks: ['raison', 'que', 'je', 'tu', 'aies', 'sans', "n'", 'pense', 'pas', 'tort', 'le'], answer: 'Je ne pense pas que tu aies tort sans raison.' },
  { id: 'b2-rmo-2', chunks: ['devrais', 'tu', 'me', 'hier', 'prévenir', 'aurais', 'si', 'voulu', 'éviter', 'tu', 'ce problème'], answer: 'Tu aurais dû me prévenir hier si tu voulais éviter ce problème.' },
  { id: 'b2-rmo-3', chunks: ["qu'", 'promis', 'il', 'serait', 'arrivé', 'il', 'tôt', 'avait'], answer: "Il avait promis qu'il serait arrivé tôt." },
  { id: 'b2-rmo-4', chunks: ['ne', 'pas', 'comprenant', 'documents', 'les', 'rapidement', "j'ai", 'pu', 'avancer', 'pas'], answer: "Ne comprenant pas rapidement les documents, je n'ai pas pu avancer." },
  { id: 'b2-rmo-5', chunks: ['a', 'par', 'décision', 'prise', 'été', 'cette', 'urgence', "l'équipe", 'en'], answer: "Cette décision a été prise par l'équipe en urgence." },
  { id: 'b2-rmo-6', chunks: ['bien', "qu'", 'fasse', 'il', 'tout', 'encore', 'comme', 'jamais', 'cela', 'que', 'semble', 'difficile'], answer: "Bien qu'il fasse tout comme jamais, cela semble encore difficile." },
  { id: 'b2-rmo-7', chunks: ['su', "j'", 'aurais', 'pas', 'cette situation', 'aurait', 'été', 'différente', 'si', 'avais'], answer: "Cette situation aurait été différente si j'avais su." },
  { id: 'b2-rmo-8', chunks: ['les', 'pouvais', 'expliquer', 'je', 'pas', 'leur', 'clairement', 'tous', 'à', 'ne'], answer: 'Je ne pouvais pas tous leur expliquer clairement.' },
  { id: 'b2-rmo-9', chunks: ['étais', 'tu', 'resté', 'si', 'aurions', 'pu', 'discuter', 'plus', 'longtemps', 'nous'], answer: 'Si tu étais resté, nous aurions pu discuter plus longtemps.' },
  { id: 'b2-rmo-10', chunks: ['aurait', 'cette idée', 'acceptée', 'plus facilement', 'avait', 'été', 'elle', 'expliquée', 'si'], answer: 'Cette idée aurait été acceptée plus facilement si elle avait été expliquée.' },
];

const LEVELS_DATA = { A1: A1_EXERCISES, A2: A2_EXERCISES, B1: B1_EXERCISES, B2: B2_EXERCISES };
const LEVELS = ['A1', 'A2', 'B1', 'B2'];

const ENCOURAGEMENTS_OK = ['Bravo !', 'Excellent !', 'Parfait !', 'Magnifique !', 'Très bien joué !'];
const ENCOURAGEMENTS_KO = [
  'Essaie encore !',
  'Bonne tentative — observe bien la phrase correcte.',
  'Presque ! Lis le modèle et réessaie à voix haute.',
  'Bon effort — relis la phrase à voix haute.',
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Normalize for forgiving comparison: lowercase, trim, collapse spaces,
// unify apostrophes, strip terminal punctuation.
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[’‘`]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .replace(/[.?!,;:]+$/, '')
    .trim();

const speak = (text) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(text);
    utter.lang = 'fr-FR';
    utter.rate = 0.95;
    window.speechSynthesis.speak(utter);
  } catch {
    /* silent — TTS unavailable */
  }
};

function ExerciseCard({ exercise, index, total, scrambled }) {
  const [value, setValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleCheck = () => {
    if (!value.trim()) return;
    const ok = normalize(value) === normalize(exercise.answer);
    setIsCorrect(ok);
    setFeedback(ok ? pick(ENCOURAGEMENTS_OK) : pick(ENCOURAGEMENTS_KO));
    setSubmitted(true);
  };

  const handleRetry = () => {
    setValue('');
    setSubmitted(false);
    setIsCorrect(false);
    setFeedback('');
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E8E4DC] overflow-hidden shadow-sm"
      data-testid={`exercise-${exercise.id}`}
    >
      {/* Header strip */}
      <div className="px-6 pt-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] text-[#999] font-medium tracking-wide">
            Phrase {index + 1} sur {total}
          </span>
          <span className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-wider">
            Remets dans l'ordre
          </span>
        </div>
        <div className="w-full h-1 bg-[#F3F4F6] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-[#D4AF37] transition-all duration-500"
            style={{ width: `${((index + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Scrambled chunks */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl px-4 py-5 min-h-[88px]"
          data-testid={`scrambled-${exercise.id}`}
        >
          {scrambled.map((chunk, i) => (
            <span
              key={`${chunk}-${i}`}
              className="inline-flex items-center bg-white border border-[#D4AF37]/40 text-[#1A2F4A] rounded-full px-4 py-2 text-base font-medium shadow-sm"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {chunk}
            </span>
          ))}
        </div>

        {/* Input */}
        <div className="space-y-2">
          <label className="text-[11px] text-[#6B4E37] font-medium tracking-wide" htmlFor={`input-${exercise.id}`}>
            Écris la phrase correcte
          </label>
          <textarea
            id={`input-${exercise.id}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!submitted) handleCheck();
              }
            }}
            placeholder="Tape la phrase ici…"
            disabled={submitted && isCorrect}
            rows={2}
            className="w-full border border-[#E8E4DC] rounded-xl px-4 py-3 text-base text-[#1A2F4A] placeholder-[#ccc] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none leading-relaxed disabled:opacity-60"
            data-testid={`input-${exercise.id}`}
          />
        </div>

        {/* Action buttons (pre-check) */}
        {!submitted && (
          <button
            onClick={handleCheck}
            disabled={!value.trim()}
            className="w-full bg-[#1A2F4A] hover:bg-[#243a58] disabled:bg-[#E8E4DC] disabled:cursor-not-allowed text-white rounded-xl py-3.5 text-sm font-bold transition-colors flex items-center justify-center gap-2"
            data-testid={`verify-${exercise.id}`}
          >
            <Check className="w-4 h-4" />
            Vérifier
          </button>
        )}

        {/* Feedback + answer */}
        {submitted && (
          <div className="space-y-4">
            <div
              className={`rounded-xl px-5 py-3.5 flex items-center gap-3 border ${
                isCorrect
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
              data-testid={`feedback-${exercise.id}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCorrect ? 'bg-emerald-100' : 'bg-amber-100'
                }`}
              >
                {isCorrect ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Sparkles className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <p
                className={`text-sm font-bold ${
                  isCorrect ? 'text-emerald-700' : 'text-amber-700'
                }`}
              >
                {feedback}
              </p>
            </div>

            <div
              className="bg-[#1A2F4A] rounded-xl px-5 py-4 space-y-3"
              data-testid={`answer-${exercise.id}`}
            >
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">
                Phrase correcte
              </p>
              <p
                className="text-base text-white/95 leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {exercise.answer}
              </p>
              <button
                onClick={() => speak(exercise.answer)}
                className="inline-flex items-center gap-2 text-[11px] font-semibold text-[#D4AF37] hover:text-white transition-colors"
                data-testid={`listen-${exercise.id}`}
              >
                <Volume2 className="w-3.5 h-3.5" />
                Écouter la phrase
              </button>
            </div>

            <div
              className="flex items-start gap-2.5 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3"
              data-testid={`conseil-${exercise.id}`}
            >
              <Lightbulb className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#6B4E37] leading-relaxed">
                Lis la phrase à voix haute deux fois — c'est comme ça que ton oreille mémorise la
                bonne structure.
              </p>
            </div>

            <button
              onClick={handleRetry}
              className="w-full flex items-center justify-center gap-2 bg-white border border-[#E8E4DC] text-[#1A2F4A] rounded-xl py-3 text-xs font-bold hover:border-[#D4AF37] transition-colors"
              data-testid={`retry-${exercise.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Réessayer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RemetsEnOrdre() {
  const [level, setLevel] = useState('A1');

  // Pre-shuffle chunks once per level so order is stable while the user types.
  const exercises = useMemo(
    () =>
      LEVELS_DATA[level].map((ex) => {
        const scrambled = [...ex.chunks];
        // Fisher-Yates
        for (let i = scrambled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [scrambled[i], scrambled[j]] = [scrambled[j], scrambled[i]];
        }
        // Guarantee scrambled differs from original chunk order when possible
        if (
          scrambled.length > 1 &&
          scrambled.every((c, i) => c === ex.chunks[i])
        ) {
          [scrambled[0], scrambled[1]] = [scrambled[1], scrambled[0]];
        }
        return { ...ex, scrambled };
      }),
    [level]
  );

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[560px] mx-auto px-6">
          {/* Breadcrumb */}
          <div
            className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap"
            data-testid="breadcrumb"
          >
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">
              Speaking Lab
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span>Atelier d'Expression</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Remets dans l'ordre</span>
          </div>

          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <Shuffle className="w-4 h-4 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Remets les mots dans l'ordre&nbsp;!
            </h1>
            <p className="text-xs text-[#6B4E37] italic max-w-sm mx-auto leading-relaxed">
              Lis les mots mélangés et reconstruis la phrase française correcte. Écris ta réponse,
              puis appuie sur <span className="font-bold">Vérifier</span>.
            </p>
          </div>

          {/* Level tabs */}
          <div className="flex justify-center gap-2 mt-4" data-testid="level-tabs">
            {LEVELS.map((lv) => (
              <button
                key={lv}
                onClick={() => setLevel(lv)}
                className={`text-[11px] font-bold tracking-wider rounded-full px-4 py-1.5 transition-all ${
                  level === lv
                    ? 'bg-[#1A2F4A] text-white shadow-sm'
                    : 'bg-white text-[#1A2F4A] border border-[#E8E4DC] hover:border-[#D4AF37]'
                }`}
                data-testid={`level-tab-${lv}`}
              >
                {lv}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Exercises */}
      <section className="pb-20">
        <div className="max-w-[560px] mx-auto px-6 space-y-5" data-testid="exercises-list">
          {exercises.map((ex, i) => (
            <ExerciseCard
              key={ex.id}
              exercise={ex}
              index={i}
              total={exercises.length}
              scrambled={ex.scrambled}
            />
          ))}

          {/* End-of-set note */}
          <div className="bg-white border border-[#E8E4DC] rounded-2xl p-6 text-center" data-testid="end-of-set">
            <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <p
              className="text-sm font-bold text-[#1A2F4A] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Bien joué&nbsp;!
            </p>
            <p className="text-xs text-[#6B4E37] leading-relaxed max-w-xs mx-auto">
              Tu as travaillé ta syntaxe française à l'oral comme à l'écrit. Continue à pratiquer pour gagner en aisance et en spontanéité.
            </p>
            <Link
              to="/speaking-lab"
              className="inline-flex items-center gap-2 mt-4 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-5 py-2 text-xs font-bold transition-colors"
              data-testid="back-to-lab"
            >
              Retour au Speaking Lab
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
